import React, { PropsWithChildren, useRef } from 'react';
import _isEmpty from 'lodash/isEmpty';
import Message from '@app/component/message/Message';
import Tag from '@app/component/tag/Tag';
import _capitalize from 'lodash/capitalize';
import Utilities from '@util/Utilities';
import ButtonLinkList from '@app/component/button-link-list/ButtonLinkList';
import AlertType from '@app/model/alert/AlertType';
import useDialogue from '@app/component/overlays/dialog/DialogueRef';
import Dialogue from '@app/component/overlays/dialog/Dialogue';
import useToaster from '@app/component/overlays/toaster/ToasterHook';
import { useTranslation } from '@app/component/translation/Translation';
import AlertLogDrawer from '@app/site/secured/rateshopper/rates/alert/drawer/AlertLogDrawer';
import HotelRoomMappingType from '@app/model/market_state/HotelRoomMappingType';
import CompanyHotel from '@app/model/company_hotel/CompanyHotel';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertQueryKeys } from '@app/model/alert/service/AlertQueryKeys';
import AlertService from '@app/model/alert/service/AlertService';
import AlertOutput from '@app/model/alert/AlertOutput';
import { useSelectedCompanyHotel } from '@app/site/secured/rateshopper/rates/provider/useSelectedCompanyHotel';
import Refunding from '@app/model/market_state/Refunding';
import TablePlaceholder from '@app/component/placeholder/TablePlaceholder';
import ParityPriceComparisonAlertRuleDifferenceType
	from '@app/model/alert/ParityPriceComparisonAlertRuleDifferenceType';

const AlertsPage = React.memo(() => {

	return <CustomAlerts />;

});

const CustomAlerts = React.memo(() => {

	const { companyHotel } = useSelectedCompanyHotel();

	if (!!companyHotel) {

		const { data: alertOutputs, isLoading, isSuccess } = useQuery({
			queryFn: async () => {
				return AlertService.findAlerts(companyHotel.id).then(data => {
					return Promise.resolve(!data ? [] : data);
				});
			},
			queryKey: AlertQueryKeys.list(companyHotel.id),
			initialData: () => [],
		});

		if (isLoading) {
			return <TablePlaceholder />;

		} else if (isSuccess) {
			return <Alerts companyHotel={companyHotel} alertOutputs={alertOutputs} />;
		}
	}
	return <></>;
});

const Alerts: React.FunctionComponent<{ companyHotel: CompanyHotel, alertOutputs: AlertOutput[] }> = ({
																																																				companyHotel,
																																																				alertOutputs,
																																																			}) => {

	const logDrawer = useRef<any>(null);
	const dialog = useDialogue<number>();
	const toaster = useToaster();
	const { t } = useTranslation();

	const queryClient = useQueryClient();
	const removeAlertMutation = useMutation({
		mutationFn: AlertService.removeAlert,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: AlertQueryKeys.lists() });
			toaster.success('Delete Alert', 'Alert deleted successfully!');
		},
		onError: (e: string) => toaster.error('Delete Alert', t(e)),
	});

	const removeAlert = () => {
		const alertId = dialog.getValue();
		removeAlertMutation.mutate({ companyHotelId: companyHotel.id, alertId: alertId! });
	};

	const findRoomTypeName = (roomType: HotelRoomMappingType) => {
		if (!!companyHotel.roomMappingTypeConfigurations) {
			const item = companyHotel.roomMappingTypeConfigurations.find(c => c.type === roomType);
			if (!!item) return Utilities.snakeCaseToWords(item.name);
		}
		return '-';
	};

	const renderAlertType = (alertType: AlertType) => {
		switch (alertType) {
			case AlertType.PRICE_COMPARISON:
				return 'Rate Comparison';
			case AlertType.COMPETITORS_PRICE_RATE:
				return 'Competitors Rate Change';
			case AlertType.PARITY_PRICE_COMPARISON:
				return 'Parity Price Comparison';
			case AlertType.COMPETITORS_SOLD_OUT:
				return 'Sold Out Competitors';
		}
	};

	return <>
		<Wrapper>

			<div
				className="mt-4 pb-3 flex flex-wrap items-center justify-between sm:flex-nowrap border-b border-gray-200 py-8">
				<div><h3 className="text-base font-semibold leading-6 text-gray-900">Get notified for market
					changes</h3><p
					className="text-sm text-gray-500">Customize the alerts below to trigger them as desired.</p>
				</div>
				<div className="ml-4 mt-4 flex-shrink-0">
					<ButtonLinkList items={[
						{ name: 'Rate Comparison', link: `/secured/rateshopper/rates/alert/create/${AlertType.PRICE_COMPARISON}` },
						{
							name: 'Competitors Rate Change',
							link: `/secured/rateshopper/rates/alert/create/${AlertType.COMPETITORS_PRICE_RATE}`,
						},
						{
							name: 'Parity Price Comparison',
							link: `/secured/rateshopper/rates/alert/create/${AlertType.PARITY_PRICE_COMPARISON}`,
						},
						{
							name: 'Sold Out Competitors',
							link: `/secured/rateshopper/rates/alert/create/${AlertType.COMPETITORS_SOLD_OUT}`,
						},
					]}>Add New Alert</ButtonLinkList>
				</div>
			</div>

			{_isEmpty(alertOutputs) && <Message state="info" extraClassName="mt-8 ring-1 ring-blue-500">
				No alerts found!
			</Message>}
			{!_isEmpty(alertOutputs) && <div
				className="mx-auto mt-8 max-w-2xl space-y-16 sm:space-y-20 lg:mx-0 lg:max-w-none bg-white p-4 sm:p-10 rounded-xl">
				<div>

					<div className="flow-root">
						<div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
							<div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
								<table className="min-w-full divide-y divide-gray-300 text-gray-500">
									<thead>
									<tr>
										<th scope="col"
												className="whitespace-normal py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-0">Name
										</th>
										<th scope="col"
												className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-0">Created
										</th>
										<th scope="col"
												className="px-2 py-3.5 text-left text-sm font-semibold text-gray-900">Type
										</th>
										<th scope="col" colSpan={2}
												className="whitespace-normal px-2 py-3.5 text-left text-sm font-semibold text-gray-900">Ruleset
										</th>
										<th scope="col"
												className="whitespace-normal px-2 py-3.5 text-left text-sm font-semibold text-gray-900">Room
											Types
										</th>
										<th scope="col"
												className="px-2 py-3.5 text-left text-sm font-semibold text-gray-900">Rate
										</th>
										<th scope="col"
												className="px-2 py-3.5 text-left text-sm font-semibold text-gray-900">Status
										</th>
										<th scope="col"
												className="relative py-3.5 pl-3 pr-4 sm:pr-0"></th>
									</tr>
									</thead>
									<tbody className="divide-y divide-gray-200 bg-white">
									{alertOutputs!.map(alertOutput => <tr key={alertOutput.alert.id}>
										<td className="whitespace-normal py-3 pl-4 pr-3 sm:pl-0 text-sm font-medium text-gray-900">
											{alertOutput.alert.name}
										</td>
										<td
											className="px-2 py-3 text-sm">{Utilities.formatDateString(alertOutput.alert.creationDate, 'DD/MM/YYYY HH:mm')}</td>
										<td
											className="px-2 py-3 text-sm">{renderAlertType(alertOutput.alert.type)}</td>
										<TableSegmentPerAlertType companyHotel={companyHotel} alertOutput={alertOutput} />
										<td className="whitespace-normal px-2 py-3 text-sm">
											{alertOutput.alert.type === AlertType.PARITY_PRICE_COMPARISON ? '-' : _isEmpty(alertOutput.alert.filter.roomTypes) ? 'All Room Types' : alertOutput.alert.filter.roomTypes.map(rt => findRoomTypeName(rt)).join(', ')}
										</td>

										<td className="whitespace-normal px-2 py-3 text-sm">
											{alertOutput.alert.filter.refunding === Refunding.FULL ? 'Lowest Flex' : 'Lowest'}
										</td>
										<td className="px-2 py-3 text-sm">
											<Tag state="success">{_capitalize(alertOutput.alert.state)}</Tag>
										</td>
										<td className="px-2 py-3 text-sm">
											<ButtonLinkList portal items={[
												{
													name: 'Edit',
													link: `/secured/rateshopper/rates/alert/${alertOutput.alert.type}/${alertOutput.alert.id}`,
												},
												{
													name: 'Duplicate',
													link: `/secured/rateshopper/rates/alert/duplicate/${alertOutput.alert.type}/${alertOutput.alert.id}`,
												},
												{ name: 'Delete', onClick: () => dialog.open(alertOutput.alert.id) },
												{ name: 'Log', onClick: () => logDrawer.current!.open(alertOutput.alert) },
											]} />
										</td>
									</tr>)}
									</tbody>
								</table>
							</div>
						</div>
					</div>
				</div>
			</div>}
		</Wrapper>
		<Dialogue ref={dialog.ref} title="Delete Alert" onConfirm={removeAlert}>
			Are you sure you want to delete this alert?
		</Dialogue>
		<AlertLogDrawer ref={logDrawer} />
	</>;
};

const Wrapper: React.FunctionComponent<PropsWithChildren> = React.memo(({ children }) => {
	return <>
		<main>
			<div className="mx-auto max-w-7xl py-6 px-4 sm:px-6 lg:px-8">
				{children}
			</div>
		</main>
	</>;
});

const TableSegmentPerAlertType: React.FunctionComponent<{
	companyHotel: CompanyHotel,
	alertOutput: AlertOutput
}> = ({ companyHotel, alertOutput }) => {

	const {
		alert,
		competitors,
		priceComparisonAlertRule,
		competitorsPriceRateAlertRule,
		parityPriceComparisonAlertRule,
		competitorsSoldOutAlertRule,
	} = alertOutput;

	const ruleDescription = (description: string, details: string) => {
		return <div className="flex flex-col">
			<span className="text-sm">{description}</span>
			<span className="italic text-xs text-gray-400">({details})</span>
		</div>;
	};

	// --- PARITY PRICE COMPARISON ---
	if (alert.type === AlertType.PARITY_PRICE_COMPARISON) {
		const providers = parityPriceComparisonAlertRule?.providers ?? [];
		const firstProv = providers[0] ?? '—';
		const moreProv =
			providers.length > 1
				? `(+ ${providers.length - 1} more)`
				: null;

		return (
			<>
				<td className="px-2 py-3 text-sm">
					<div className="flex flex-col">
						<span>{firstProv}</span>
						{moreProv && (
							<span className="italic text-xs text-gray-400">{moreProv}</span>
						)}
					</div>
				</td>
				<td className="px-2 py-3 text-sm">
					{ruleDescription(
						`More than ${parityPriceComparisonAlertRule?.triggerDifference ?? 0}${parityPriceComparisonAlertRule?.differenceType === ParityPriceComparisonAlertRuleDifferenceType.AMOUNT ? Utilities.formatCurrency(companyHotel.settings.main.currency) : '%'}`,
						`for ${parityPriceComparisonAlertRule?.ignorePartnerOffers ? 'non-brand' : ''} selected providers`,
					)}
					'for selected providers'
				</td>
			</>
		);
	}

	// For PRICE_COMPARISON and COMPETITORS_PRICE_RATE:
	const name = competitors?.[0]?.name ?? '—';
	const moreCompetitors =
		competitors != null && competitors.length > 1
			? `(+ ${competitors.length - 1} more)`
			: null;

	return (
		<>
			<td className="px-2 py-3 text-sm">
				<div className="flex flex-col">
					<span>{name}</span>
					{moreCompetitors && <span className="italic text-xs text-gray-400">{moreCompetitors}</span>}
				</div>
			</td>
			<td className="px-2 py-3 text-sm">
				{alert.type === AlertType.PRICE_COMPARISON &&
					ruleDescription(
						`${priceComparisonAlertRule.priceRange!.from}% to ${priceComparisonAlertRule.priceRange!.to}%`,
						`for ${priceComparisonAlertRule.trigger.toLowerCase()}`,
					)}
				{alert.type === AlertType.COMPETITORS_PRICE_RATE &&
					ruleDescription(
						`${competitorsPriceRateAlertRule.priceRatePercentage}%`,
						`for ${competitorsPriceRateAlertRule.calculation.toLowerCase()}`,
					)}
				{alert.type === AlertType.COMPETITORS_SOLD_OUT &&
					ruleDescription(
						``,
						`${competitorsSoldOutAlertRule.competitorsInclusion.toLowerCase()} Competitors`,
					)}
			</td>
		</>
	);
};

export default AlertsPage;